import {
  TransportAuthorizationKind,
  TransportAuthorizationStatus,
  TransportCommunicationMethod,
  TransportControlChecks,
  TransportManagerStatus,
} from "../../../../../../passapp-sdk/types/transportTypes";

// Modelos de vista derivados de la respuesta del backend. Los componentes sólo conocen estos
// tipos; el mapeo desde `TransportAuthorization` vive en transportAuthorizationMappers.ts.

export type AuthorizationStatus = TransportAuthorizationStatus;
export type AuthorizationKind = TransportAuthorizationKind;
export type ManagerStatus = TransportManagerStatus;
export type CommunicationMethod = TransportCommunicationMethod;

/** Datos que el guardia dejó al aprobar la autorización por fuera de la app. Compartido por
 * entrada y salida; `dock` sólo aplica a entrada. */
export type GuardApprovalInfo = {
  approvedByGuard: boolean;
  communicationMethod: CommunicationMethod | null;
  authorizedBy: string | null;
};

/** Manager de transporte mostrado en la fila (anfitrión, o los notificados si está pendiente). */
export type AuthorizationPerson = {
  user: string;
  name: string;
  phone: string;
  status: ManagerStatus;
};

/** Una autorización del backend. Se renderiza como una fila dentro de su grupo de empresa. */
export type AuthorizationRow = {
  id: string;
  kind: AuthorizationKind;
  status: AuthorizationStatus;
  people: AuthorizationPerson[];
  operationLabel: string;
  usedTrucks: number;
  totalTrucks: number;
  availableSlots: number;
  truckUsagePct: number;
  validFrom: string;
  validTo: string;
  validFromTs: number | null;
  validToTs: number | null;
  rejectionReason: string | null;
  gateObservations: string | null;
  requestedAt: number;
  /** Datos del vehículo autorizado. Las preautorizaciones suelen no traerlos: "" / null. */
  vehiclePlate: string;
  /** null equivale a "No aplica". */
  trailerPlate: string | null;
  merchandiseInTransit?: boolean;
  receipt?: string;
  seal?: string;
  /** Dock asignado a esta autorización puntual (el del grupo es el agregado de todas). */
  dock: string | number | null;
  /** Pedido de dock colgado de una preautorización: se muestra fusionado con ella, en una sola
   * tarjeta, en vez de como una fila aparte. */
  isDockRequest: boolean;
  parentAuthorizationId: string | null;
  /** true sólo si esta fila es un pedido de dock que `mergeLocalDockRequests` fusionó con su
   * preautorización: los datos del paraguas (cupo, barra, observaciones) se siguen mostrando. */
  isMergedDockRequest: boolean;
  /** Observaciones para portería heredadas de la preautorización fusionada. */
  parentObservations: string | null;
  /** Datos de la aprobación hecha por el guardia, si la hubo. */
  guardApproval: GuardApprovalInfo;
};

/** Autorizaciones de acceso agrupadas por empresa autorizante + edificio. */
export type AuthorizationGroup = {
  id: string;
  /** Id de la empresa autorizante (`TransportAuthorization.company`), para prefillear el
   * selector de "Empresa autorizante" al pedir dock. `title` sólo trae el nombre. */
  companyId: string;
  title: string;
  subtitle: string;
  contactEmail?: string;
  operationLabels: string[];
  usedTrucks: number;
  totalTrucks: number;
  availableSlots: number;
  authorizerCount: number;
  expiresAt: string;
  dock?: string | number;
  rows: AuthorizationRow[];
};

/** Patentes: siempre se piden verificar, a diferencia de los demás controles que dependen de lo
 * que marcó el autorizante en `controlChecks`. Un camión puede traer más de un acoplado, así que a
 * partir del segundo cada uno lleva su propio id (`trailerPlate-2`, `trailerPlate-3`, …) para que
 * se verifiquen de forma independiente. */
export type PlateControlKey = "vehiclePlate" | "trailerPlate" | `trailerPlate-${number}`;
export type GateControlKey = keyof TransportControlChecks | PlateControlKey;

export type GateControlItem = {
  id: GateControlKey;
  label: string;
  value?: string;
  /** Segundo dato del control, en su propia línea debajo de `value` (p. ej. la empresa del
   * custodio, debajo de su patente). */
  valueSecondary?: string;
  /** Estado local del guardia: no se persiste (no hay endpoint todavía). */
  verified: boolean;
};

/** Nota + foto opcionales que el guardia puede dejar sobre un control puntual. Viaja en
 * `addEvent` (ver `GateControlPayload`), patentes incluidas. */
export type GateControlObservation = {
  text: string;
  photo: File | null;
};

/** Un control de egreso listo para viajar en `addEvent.transportAuthorizations[].control`. `photo`
 * es el `File` que cargó el guardia; `useEvent` lo sube y lo reemplaza por el id de la imagen.
 * `plate` sólo aplica a filas de acoplado (`control.trailerPlates[].plate`): la patente tal como
 * la controló el guardia. El tractor no la necesita: es la misma patente de la autorización. */
export type GateControlPayload = {
  verified: boolean;
  observations: string;
  photo: File | null;
  plate?: string;
};

/** Todos los controles que puede mandar `addEvent`: los 4 "simples" más las patentes. */
export type TransportExitControls = Partial<Record<GateControlKey, GateControlPayload>>;

/** Orden y universo de los controles "simples" (no patente) que acepta `addEvent`. */
export const EXIT_CONTROL_KEYS: Array<keyof TransportControlChecks> = [
  "receipt",
  "seal",
  "vehicleCustody",
  "satelliteCustody",
];

export const isExitControlKey = (key: GateControlKey): key is keyof TransportControlChecks =>
  (EXIT_CONTROL_KEYS as GateControlKey[]).includes(key);

/** Filas de patente: tractor o acoplado (`trailerPlate`, `trailerPlate-2`, …). Viajan en
 * `control.vehiclePlate` / `control.trailerPlates[]`. */
export const isPlateControlKey = (key: GateControlKey): key is PlateControlKey =>
  key === "vehiclePlate" || key === "trailerPlate" || /^trailerPlate-\d+$/.test(key);

export type ExitAuthorization = {
  id: string;
  kind: AuthorizationKind;
  status: AuthorizationStatus;
  /** Id de la empresa autorizante (`TransportAuthorization.company`). `title` sólo trae el nombre. */
  companyId: string;
  title: string;
  subtitle: string;
  contactEmail?: string;
  hostName: string;
  hostPhone: string;
  people: AuthorizationPerson[];
  validFrom: string;
  validTo: string;
  validFromTs: number | null;
  validToTs: number | null;
  vehiclePlate: string;
  /** null equivale a "No aplica". */
  trailerPlate: string | null;
  driverDni: string;
  driverCountry: string;
  loaded: boolean;
  merchandiseInTransit?: boolean;
  receipt?: string;
  seal?: string;
  /** Datos del custodio vehicular. null si no se pidió ese control o no se cargaron. */
  vehicleCustodyPlate: string | null;
  vehicleCustodyCompany: string | null;
  controls: GateControlItem[];
  gateObservations?: string;
  rejectionReason: string | null;
  requestedAt: number;
  /** Datos de la aprobación hecha por el guardia, si la hubo. En egreso no aplica dock. */
  guardApproval: GuardApprovalInfo;
};

export type ExitAuthorizationGroup = {
  id: string;
  title: string;
  subtitle: string;
  contactEmail?: string;
  authorizations: ExitAuthorization[];
};

/** Los valores quedan en español: son el contrato del campo RHF `transportFlujo`. */
export type TransportFlow = "entrada" | "salida";
